import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Mail, MapPin, Phone, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';
import CallbackModal from '../components/CallbackModal';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);

  useEffect(() => {
    document.title = language === 'en'
      ? 'Contact Us | Credit Auto Reset - Auto Financing Quebec'
      : 'Contactez-nous | Credit Auto Reset - Financement Auto Québec';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      language === 'en'
        ? 'Contact Credit Auto Reset for auto financing in Quebec. Call 514-418-3968 or visit us at 1007 Boul René-Lévesque, Verdun. Free credit evaluation.'
        : 'Contactez Credit Auto Reset pour votre financement auto au Québec. Appelez 514-418-3968 ou visitez-nous au 1007 Boul René-Lévesque, Verdun. Évaluation gratuite.'
    );
  }, [language]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      navigate('/merci');
    } catch (error) {
      toast({
        title: t.contact.error,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t.contact.visitUs,
      content: '1007 Boul René-Lévesque, Verdun, Quebec H3E 0H1',
      link: 'https://maps.google.com/?q=1007+Boulevard+René-Lévesque,+Verdun,+QC'
    },
    {
      icon: Phone,
      title: t.contact.callUs,
      content: '514-418-3968',
      link: 'tel:514-418-3968'
    },
    {
      icon: Mail,
      title: language === 'en' ? 'Email' : 'Courriel',
      content: 'info@creditautoreset.ca',
      link: 'mailto:info@creditautoreset.ca'
    },
    {
      icon: Clock,
      title: language === 'en' ? 'Hours' : 'Heures',
      content: language === 'en' ? 'Mon-Thu: 9-20h | Fri: 9-18h | Sat: 10-16h' : 'Lun-Jeu: 9h-20h | Ven: 9h-18h | Sam: 10h-16h',
      link: null
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-16">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#1a5c3b] via-[#16543 2] to-green-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t.contact.title}
          </h1>
          <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#1e7d55] hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#1e7d55] to-[#1a5c3b] rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        target={info.icon === MapPin ? '_blank' : undefined}
                        rel={info.icon === MapPin ? 'noopener noreferrer' : undefined}
                        className="text-sm text-gray-600 hover:text-[#1a5c3b] transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-sm text-gray-600">{info.content}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.contact.getInTouch}
            </h2>
          </div>

          <Card className="shadow-xl border-2">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">{t.contact.nameLabel} *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t.contact.emailLabel} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t.contact.phoneLabel}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-12"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">{t.contact.messageLabel} *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="resize-none"
                  />
                </div>
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-[#1a5c3b] hover:bg-[#16543 2] text-white text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {t.contact.sending}
                      </>
                    ) : (
                      t.contact.sendMessage
                    )}
                  </Button>
                </div>

                {/* Request Callback Section */}
                <div className="pt-6 border-t border-gray-200 mt-6">
                  <p className="text-center text-gray-600 mb-4">
                    {language === 'en' ? 'Too busy to wait? Let us call you!' : 'Trop occupé pour attendre? Laissez-nous vous appeler!'}
                  </p>
                  <Button
                    type="button"
                    onClick={() => setShowCallbackModal(true)}
                    data-testid="contact-callback-btn"
                    variant="outline"
                    className="w-full h-12 border-2 border-[#1a5c3b] text-[#1a5c3b] hover:bg-[#1a5c3b] hover:text-white font-semibold transition-all duration-300"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    {t.callback.cta}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Callback Modal */}
      <CallbackModal isOpen={showCallbackModal} onClose={() => setShowCallbackModal(false)} />

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden shadow-2xl h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2797.4!2d-73.5673!3d45.4583!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDI3JzI5LjkiTiA3M8KwMzQnMDIuMyJX!5e0!3m2!1sen!2sca!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Credit Auto Reset Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;